import { useState } from 'react';
import { Pencil } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { formatPercent } from '@/lib/utils';
import type { Rates } from '@jobos/shared';

export function JobRatesDisplay({
  rates,
  onRatesChange,
}: {
  rates: Rates;
  onRatesChange?: (rates: { taxRate: number; markupRate: number }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [localTax, setLocalTax] = useState('');
  const [localMarkup, setLocalMarkup] = useState('');

  const handleOpen = (isOpen: boolean) => {
    if (isOpen) {
      setLocalTax(String(Math.round(rates.taxRate * 10000) / 100));
      setLocalMarkup(String(Math.round(rates.markupRate * 10000) / 100));
    }
    setOpen(isOpen);
  };

  const handleSave = () => {
    const parsedTax = parseFloat(localTax);
    const parsedMarkup = parseFloat(localMarkup);
    const newTax = (isNaN(parsedTax) ? 0 : parsedTax) / 100;
    const newMarkup = (isNaN(parsedMarkup) ? 0 : parsedMarkup) / 100;
    if (newTax < 0 || newTax > 1 || newMarkup < 0 || newMarkup > 1) return;
    onRatesChange?.({ taxRate: newTax, markupRate: newMarkup });
    setOpen(false);
  };

  if (!onRatesChange) {
    return (
      <span className="text-xs text-gray-500">
        Tax {formatPercent(rates.taxRate)} · Markup {formatPercent(rates.markupRate)}
      </span>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors">
          <span>Tax {formatPercent(rates.taxRate)} · Markup {formatPercent(rates.markupRate)}</span>
          <Pencil className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-3">
        <div className="space-y-3">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Job Rates
          </h4>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Tax Rate (%)</label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={localTax}
              onChange={(e) => setLocalTax(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Markup Rate (%)</label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={localMarkup}
              onChange={(e) => setLocalMarkup(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setOpen(false)}
              className="px-2.5 py-1 text-xs text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-2.5 py-1 text-xs font-medium text-white bg-gray-900 rounded hover:bg-gray-800"
            >
              Apply
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
